/**
 * @class RebuyGroupSwatch
 * A native Web Component to display a swatch of related products.
 */
class RebuyGroupSwatch extends HTMLElement {
  constructor() {
    super();
    this.products = null;
  }

  async connectedCallback() {
    const { groupId, productId, widgetId, productIndex } = this.dataset;

    if (!window.Rebuy || !window.RebuyGroupConfig || !widgetId) {
      console.error('RebuyGroupSwatch: Missing group ID or window.RebuyGroupConfig.');
      return;
    }


    this.groupId = groupId;
    this.productId = productId;
    this.widgetId = widgetId;
    this.productIndex = productIndex;

    this.rebuyWidget = window.Rebuy?.widgets.find(w => w.id == this.widgetId)

    this.liveProduct = this.rebuyWidget?.data?.products[this.productIndex];


    console.log(`Fetching data for group: ${this.groupId}`);
    const shopifyClient = new ShopifyStorefrontClient(window.RebuyGroupConfig);
    const productGroupData = await shopifyClient.getProductGroup(this.groupId);

    if (productGroupData?.node?.relatedProducts?.references) {
      const productNodes = productGroupData.node.relatedProducts.references.nodes;
      const productIds = productNodes.map(node => node.id.split('/').pop());

      if (productIds.length > 0) {
        console.log(`Found ${productIds.length} related product IDs:`, productIds);
        const rebuyClient = new RebuyClient(window.RebuyGroupConfig);
        this.products = await rebuyClient.getProductsByIDs(productIds);
        console.log('--- Final Product Data from Rebuy ---', this.products);
        this.generateHtmlSwatches();
      } else {
        console.log('No related products found for this group.');
      }
    } else {
      console.log('Could not retrieve product group data from Shopify.');
    }

  }

  // --- Helper Methods for Rendering ---

  displayColorSwatches(option) {
    return option && option.name && option.name.toLowerCase() === 'color';
  }


  _addEventListeners() {
    this.querySelectorAll('.rebuy-color-input.rebuy-color-input--combined').forEach(input => {
      input.addEventListener('change', (event) => this.handleVariantChange(event));
    });
  }

  handleVariantChange(event) {
    const { target } = event;
    event.preventDefault()
    const siblingProduct = this._getSiblingProductFromEvent(target);

    if (!this.liveProduct || !siblingProduct) {
      console.error('RebuyGroupSwatch: Product or sibling product not found for change event.');
      return;
    }

    this._updateLiveProductState(siblingProduct);

    this.rebuyWidget.render();

    console.log('RERENDER')

  }

  /**
   * Retrieves the sibling product data based on the event target.
   * @param {EventTarget} target - The event target from the change event.
   * @returns {object|undefined} The sibling product object.
   */
  _getSiblingProductFromEvent(target) {
    const productIndex = parseInt(target.dataset.productIndex, 10);
    return this.products?.[productIndex];
  }

  /**
   * Orchestrates the update of the live product's state with data from a sibling product.
   * @param {object} siblingProduct - The product data to switch to.
   */
  _updateLiveProductState(siblingProduct) {
    const newImage = { ...siblingProduct.image };

    this._updateProductIdentity(siblingProduct);
    this._updateProductImages(siblingProduct, newImage);
    this._updateProductVariants(siblingProduct, newImage);
    this._updateProductPrice(siblingProduct);
  }

  /**
   * Updates the core identity properties of the live product.
   * @param {object} siblingProduct - The product data to switch to.
   */
  _updateProductIdentity(siblingProduct) {
    this.liveProduct.id = Number(siblingProduct.id);
    this.liveProduct.handle = siblingProduct.handle;
    this.liveProduct.title = siblingProduct.title;
    this.liveProduct.vendor = siblingProduct.vendor;
    this.liveProduct.tags = siblingProduct.tags;
  }

  /**
   * Updates the image properties of the live product.
   * @param {object} siblingProduct - The product data to switch to.
   * @param {object} newImage - The new main image object.
   */
  _updateProductImages(siblingProduct, newImage) {
    this.liveProduct.image = newImage;
    this.liveProduct.images = [...siblingProduct.images];
  }


  _updateProductVariants(siblingProduct, newImage) {

    let filteredVariants = siblingProduct.variants.filter(v => this.rebuyWidget.variantAvailable(v))
    this.liveProduct.options = [...siblingProduct.options];
    this.liveProduct.variants = [...filteredVariants];


    if (this.liveProduct.selected_variant) {
      // Set selected_variant to the first variant of the new product
      if (filteredVariants?.length > 0) {
        const currentVariant = this.liveProduct.selected_variant;
        const colorOptionIndex = this.liveProduct.options.findIndex(opt => this.displayColorSwatches(opt));
        const matchingVariant = filteredVariants.find(variant => {
          return this.liveProduct.options.every((opt, index) => {
            if (index === colorOptionIndex) return true;
            const optionKey = `option${index + 1}`;
            return variant[optionKey] === currentVariant[optionKey];
          });
        });
        this.liveProduct.selected_variant = matchingVariant || filteredVariants[0];
      }

      // Ensure the new selected_variant has an image object attached,
      // as the variant data from Rebuy might not include it.
      if (this.liveProduct.selected_variant) {
        this.liveProduct.selected_variant.image = newImage;
        this.liveProduct.selected_variant.featured_image = newImage;
      }
    }
  }

  /**
   * Updates the price properties of the live product.
   * @param {object} siblingProduct - The product data to switch to.
   */
  _updateProductPrice(siblingProduct) {
    this.liveProduct.price = siblingProduct.price;
    this.liveProduct.compare_at_price = siblingProduct.compare_at_price;
  }

  generateHtmlSwatches() {
    // Rendering to Light DOM to inherit global styles
    this.innerHTML = '';
    if (!this.products) return;


    const productsHTML = this.products.map((product, swatchIndex) => {
      if (!product) return '';


      const colorOption = product.options.find(opt => this.displayColorSwatches(opt));
      if (!colorOption) return '';


      return colorOption.values.map((value) => {
        const valueSlug = value.toLowerCase().replace(/\s+/g, '-');

        const inputId = `swatch-${this.widgetId}-${this.productIndex}-${product.id}-${valueSlug}`;
        return `
          <div class="rebuy-color-swatch"> 
            <input
              type="radio"
              class="rebuy-color-input rebuy-color-input--combined  hide"
              name="widget-id-${this.widgetId}-product-index-${this.productIndex}"
              id="${inputId}"
              value="${value}"
              data-product-index="${swatchIndex}"
              ${this.productId == product.id ? 'checked' : ''}
            >
            <label
              for="${inputId}"
              class="rebuy-color-label"
              title="${value}"
            >
            </label>
          </div>
        `;
      }).join('');
    }).join('');

    this.innerHTML = productsHTML;
    this._addEventListeners();

  }
}

if (!customElements.get('rebuy-group-swatch')) {
  customElements.define('rebuy-group-swatch', RebuyGroupSwatch);
}

// --- SHOPIFY STOREFRONT CLIENT ---
class ShopifyStorefrontClient {
  constructor(config) {
    this.shop = config.shopDomain;
    this.token = config.storefrontAccessToken;
    // The cache is now managed by sessionStorage.
  }

  async query(query, variables = {}) {
    // Use a prefixed key to avoid conflicts in sessionStorage
    const cacheKey = `rebuy_sf_cache_${JSON.stringify({ query, variables })}`;

    const cachedItem = sessionStorage.getItem(cacheKey);
    if (cachedItem) {
      try {
        // Attempt to parse and return the cached data
        return JSON.parse(cachedItem);
      } catch (e) {
        // If parsing fails, the data is corrupt, so remove it.
        sessionStorage.removeItem(cacheKey);
      }
    }

    try {
      const response = await fetch(`https://${this.shop}/api/2023-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({ query, variables }),
      });

      const json = await response.json();
      console.log("🚀 ~ ShopifyStorefrontClient ~ query ~ json:", json)
      if (json.errors) throw new Error(JSON.stringify(json.errors));

      try {
        // Store the successful response in sessionStorage
        sessionStorage.setItem(cacheKey, JSON.stringify(json.data));
      } catch (e) {
        console.error('Rebuy Cache: Failed to write to sessionStorage. Storage might be full.', e);
      }

      return json.data;
    } catch (error) {
      console.error('Shopify API Error:', error);
      return null;
    }
  }

  async getProductGroup(metaobjectId) {
    const query = `
        query getMetaobject($id: ID!) {
          node(id: $id) {
            ... on Metaobject {
              id
              relatedProducts: field(key: "related_products") {
                value
                references(first: 10) {
                  nodes {
                    ... on Product {
                      id
                      title
                      handle
                    }
                  }
                }
              }
            }
          }
        }
      `;
    try {
      return await this.query(query, { id: metaobjectId });

    } catch (error) {
      console.error('Error fetching product group:', error);
      return null;
    }
  }
}

// --- REBUY CLIENT ---
class RebuyClient {
  constructor(config) {
    this.apiKey = config.rebuyApiKey;
    this.shop = config.shopDomain;
    this.productCache = new Map();
  }

  async getProductsByIDs(productIds) {
    const cacheKey = `rebuy_products_cache_${[...productIds].sort().join(',')}`;
    const cachedItem = sessionStorage.getItem(cacheKey);

    let productsData = null;

    if (cachedItem) {
      try {
        productsData = JSON.parse(cachedItem);
      } catch (e) {
        console.error('Rebuy Cache: Failed to parse cached product data. Removing item.', e);
        sessionStorage.removeItem(cacheKey);
      }
    }

    if (!productsData) {
      try {
        const url = `https://rebuyengine.com/api/v1/products/static?ids=${productIds.join(',')}&key=${this.apiKey}&format=pretty&filter_oos=true`;
        const res = await fetch(url);
        const json = await res.json();

        if (json.data) {
          productsData = json.data;
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(productsData));
          } catch (e) {
            console.error('Rebuy Cache: Failed to write to sessionStorage. Storage might be full.', e);
          }
        }
      } catch (e) {
        console.error("Rebuy Fetch Error", e);
      }
    }

    if (productsData) {
      productsData.forEach(p => this.productCache.set(p.id.toString(), p));
    }
    return productIds.map(id => this.productCache.get(id.toString())).filter(Boolean);
  }
}




(() => {
  // let timeout = false;
  // let delay = 250;
  const backdrop = document.querySelector(".rebuy-backdrop");

  window.Rebuy7Q81 = {
    openBackdrop: function () {
      backdrop.style.display = "block";
      backdrop.classList.add("open");
      document.querySelector("body").style.overflow = "hidden";
    },
    closeBackdrop: function () {
      backdrop.style.display = "none";
      document.body.classList.remove("quick-add-open");
      backdrop.classList.remove("open");
      document.querySelector("body").style.overflow = "auto";
    },
  };

  // function updateBackdrop() {
  // 	if (window.innerWidth < 769) {
  // 		if(document.body.classList.contains('quick-add-open')) {
  // 			openBackdrop();
  // 		}
  // 	} else {
  // 		closeBackdrop();
  // 	}
  // }

  // window.addEventListener('resize', () => {
  // 	clearTimeout(timeout);
  // 	timeout = setTimeout(updateBackdrop, delay);
  // });

  backdrop.addEventListener("click", (event) => {
    var elem = document.querySelector(".open-quick-add .rebuy-hover-options");
    if (elem && !elem.contains(event.target)) {
      let close = elem.querySelector(".btn-close");
      close.click();
    }
  });
})();
